import { EyeInvisibleOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useAuthenticate, usePostLogout } from "../api/api";
import { useMessage } from "../components/notifications/message";
import { getRecaptchaKey, isRecaptchaEnabled } from "../utils/app";
import {
  EXHUT_HUB_LOCAL_STORAGE_TOKEN,
  EXHUT_REDIRECT,
  ROUTES,
} from "../utils/constants";

export default function LoginPage2() {
  const { mutateAsync: loginAsync, isLoading, isError } = useAuthenticate();

  const { mutateAsync: logoutAsync } = usePostLogout();

  const navigate = useNavigate();
  const [recaptchaVerified, setRecaptchaVerified] = useState(
    !isRecaptchaEnabled
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const isLogout = new URLSearchParams(searchParams).get("logout");

  const { notification, messageError } = useMessage();

  useEffect(() => {
    if (isLogout) {
      searchParams.delete("logout");
      setSearchParams(searchParams);
      logoutAsync().catch(() =>
        messageError(
          "Whoops! Unable to signout. Please contact support@expensehut.com"
        )
      );
    }
  }, [isLogout]);

  const formik = useFormik({
    initialValues: {
      password: "",
      email: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Please enter password."),
      email: Yup.string().required("Please enter email"),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      loginAsync({
        email: values.email.toLowerCase(),
        password: values.password,
      })
        .then((resp) => {
          localStorage.setItem(EXHUT_HUB_LOCAL_STORAGE_TOKEN, resp.sign);
          const redirect = localStorage.getItem(EXHUT_REDIRECT);
          localStorage.removeItem(EXHUT_REDIRECT);
          if (redirect && redirect !== "/login") {
            navigate(redirect);
          } else if (resp.admin === true) {
            navigate(ROUTES.ADMIN);
          } else {
            navigate(ROUTES.HOME);
          }
        })
        .catch((e) => {
          messageError(
            "The email or password entered does not match our records."
          );
        });
    },
  });

  const setLoginReady = (e: any) => {
    // console.log("Ready recaptch", )
    setRecaptchaVerified(true);
  };

  return (
    <div className="min-h-screen mx-auto max-w-5xl">
      {notification}

      <div className="flex justify-center my-20">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <form
            onSubmit={formik.handleSubmit}
            action="submit"
            className="space-y-6"
          >
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome Back
              </h1>
              <p className="text-gray-600">
                Sign in to your Expensehut account
              </p>
            </div>

            {/* Username Field */}
            <div className="space-y-2">
              <Input
                {...formik.getFieldProps("email")}
                id="email"
                placeholder="Email..."
                status={!!formik.errors.email ? "error" : undefined}
                size="large"
                className="w-full"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700 transition"
                >
                  Forgot Password?
                </Link>
              </div>
              <Input.Password
                {...formik.getFieldProps("password")}
                id="password"
                placeholder="Password..."
                status={!!formik.errors.password ? "error" : undefined}
                iconRender={(visible) =>
                  visible ? <EyeInvisibleOutlined /> : <EyeInvisibleOutlined />
                }
                size="large"
              />
            </div>

            {isRecaptchaEnabled && (
              <div style={{}} className="mx-0 my-20">
                <ReCAPTCHA
                  sitekey={getRecaptchaKey()}
                  onChange={setLoginReady}
                />
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="primary"
              onClick={formik.submitForm}
              disabled={isLoading}
              loading={isLoading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              Sign In
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Don't have an account?
              </span>
            </div>
          </div>

          {/* Sign Up Link */}
          <Link to="/register">
            <Button
              type="dashed"
              className="w-full h-11 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Create Account
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  formRowMin: {
    display: "flex",
    margin: "5px 10px",
  },
  formRow: {
    marginTop: 30,
    display: "flex",
  },
  formColumn: {
    marginTop: 30,
    display: "flex",
    flexDirection: "column",
  },
};
